import React, { useState, useEffect } from 'react';
import { Search, Plus, Upload, BarChart3, Eye, ThumbsUp, Edit, Trash2, Download, FileText } from 'lucide-react';
import faqAPI, { FAQ, FAQCategory, BulkUploadResult, AnalyticsSummary } from '../services/faqAPI';
import { useAuth } from '../contexts/AuthContext';
import toastService from '../services/toastService';

const FAQManagement: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'browse' | 'add' | 'bulk' | 'analytics' | 'categories'>('browse');
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);

  const [newFAQ, setNewFAQ] = useState({
    question: '',
    answer: '',
    category_id: '',
    source_reference: '',
    tags: '',
    is_published: true
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    icon: 'BookOpen'
  });

  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkUploadResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);

  useEffect(() => {
    loadCategories();
    loadFAQs();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await faqAPI.getCategories();
      setCategories(data);
    } catch (error: any) {
      console.error('Failed to load categories:', error);
      if (error.response?.status === 401) {
        console.error('Authentication error - please log in again');
      }
    }
  };

  const loadFAQs = async () => {
    try {
      setLoading(true);
      const response = await faqAPI.getFAQs({ limit: 100 });
      setFaqs(response.faqs);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const data = await faqAPI.getAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadFAQs();
      return;
    }
    try {
      setLoading(true);
      const results = await faqAPI.searchFAQs(searchQuery, selectedCategory || undefined);
      setFaqs(results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFAQ = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      await faqAPI.createFAQ({
        ...newFAQ,
        category_id: newFAQ.category_id ? parseInt(newFAQ.category_id) : undefined,
        tags: newFAQ.tags ? newFAQ.tags.split(',').map(t => t.trim()) : []
      });
      toastService.success('FAQ created successfully!');
      setNewFAQ({
        question: '',
        answer: '',
        category_id: '',
        source_reference: '',
        tags: '',
        is_published: true
      });
      loadFAQs();
      setActiveTab('browse');
    } catch (error: any) {
      console.error('Failed to create FAQ:', error);
      if (error.response?.data?.error?.includes('already exists')) {
        toastService.error('⚠️ Duplicate FAQ: This question already exists in your knowledge base');
      } else {
        toastService.error('Failed to create FAQ. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateFAQ = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFAQ) return;

    try {
      await faqAPI.updateFAQ(editingFAQ.id, {
        question: editingFAQ.question,
        answer: editingFAQ.answer,
        category_id: editingFAQ.category_id,
        source_reference: editingFAQ.source_reference,
        tags: editingFAQ.tags,
        is_published: editingFAQ.is_published
      });
      setEditingFAQ(null);
      loadFAQs();
    } catch (error) {
      console.error('Failed to update FAQ:', error);
    }
  };

  const handleDeleteFAQ = async (id: number) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await faqAPI.deleteFAQ(id);
      loadFAQs();
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
    }
  };

  const handleBulkUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkFile || uploadingBulk) return;

    try {
      setUploadingBulk(true);
      const result = await faqAPI.bulkUploadFAQs(bulkFile);
      setBulkResult(result);
      setBulkFile(null);
      loadFAQs();
    } catch (error) {
      console.error('Bulk upload failed:', error);
    } finally {
      setUploadingBulk(false);
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creatingCategory) return;

    try {
      setCreatingCategory(true);
      await faqAPI.createCategory({
        name: newCategory.name,
        slug: newCategory.name.toLowerCase().replace(/\s+/g, '-'),
        description: newCategory.description,
        icon: newCategory.icon
      });
      setNewCategory({
        name: '',
        description: '',
        icon: 'BookOpen'
      });
      loadCategories();
    } catch (error) {
      console.error('Failed to create category:', error);
    } finally {
      setCreatingCategory(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'Question,Answer,Category,Source Reference,Tags,Is Published\n' +
      '"How do I open the template?","Download from Documents section...","Template Usage","User Guide v2.3","template,excel",true\n' +
      '"How do I write a risk statement?","Follow [Event] + [Impact] + [Consequence]","Risk Writing","Risk Guide","risk,writing",true';

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'faq_template.csv';
    a.click();
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab]);

  const filteredFAQs = faqs.filter(faq => {
    if (selectedCategory && faq.category_id !== selectedCategory) return false;
    if (searchQuery) {
      return faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
             faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  return (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="bg-gradient-to-r from-super-dark-purple to-dusky-purple text-white p-8 rounded-t-lg">
        <h1 className="text-3xl font-bold mb-2">FAQ Management Center</h1>
        <p className="text-gray-300">Manage your JARVIS knowledge base, track usage analytics, and reduce support burden</p>
      </div>

      <div className="border-b border-gray-200">
        <div className="flex space-x-1 px-6">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'browse'
                ? 'text-dusky-purple border-b-2 border-electric-blue'
                : 'text-gray-600 hover:text-super-dark-purple'
            }`}
          >
            Browse & Edit FAQs
          </button>
          <button
            onClick={() => setActiveTab('add')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'add'
                ? 'text-dusky-purple border-b-2 border-electric-blue'
                : 'text-gray-600 hover:text-super-dark-purple'
            }`}
          >
            Add New FAQ
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'bulk'
                ? 'text-dusky-purple border-b-2 border-electric-blue'
                : 'text-gray-600 hover:text-super-dark-purple'
            }`}
          >
            Bulk Upload
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'analytics'
                ? 'text-dusky-purple border-b-2 border-electric-blue'
                : 'text-gray-600 hover:text-super-dark-purple'
            }`}
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'categories'
                ? 'text-dusky-purple border-b-2 border-electric-blue'
                : 'text-gray-600 hover:text-super-dark-purple'
            }`}
          >
            Categories
          </button>
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'browse' && (
          <div>
            <div className="mb-6 flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search FAQs by question, answer, or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue"
                />
              </div>
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <button
                onClick={handleSearch}
                className="px-6 py-2 bg-dusky-purple text-white rounded-lg hover:bg-dusky-purple transition"
              >
                Search
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-electric-blue"></div>
              </div>
            ) : filteredFAQs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No FAQs found. Click "Add New FAQ" to create your first one.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFAQs.map(faq => (
                  <div key={faq.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition">
                    {editingFAQ?.id === faq.id ? (
                      <form onSubmit={handleUpdateFAQ} className="space-y-4">
                        <input
                          type="text"
                          value={editingFAQ.question}
                          onChange={(e) => setEditingFAQ({ ...editingFAQ, question: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          placeholder="Question"
                        />
                        <textarea
                          value={editingFAQ.answer}
                          onChange={(e) => setEditingFAQ({ ...editingFAQ, answer: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          rows={4}
                          placeholder="Answer"
                        />
                        <div className="flex gap-4">
                          <select
                            value={editingFAQ.category_id || ''}
                            onChange={(e) => setEditingFAQ({ ...editingFAQ, category_id: e.target.value ? parseInt(e.target.value) : undefined })}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                          >
                            <option value="">No Category</option>
                            {categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={editingFAQ.source_reference || ''}
                            onChange={(e) => setEditingFAQ({ ...editingFAQ, source_reference: e.target.value })}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                            placeholder="Source Reference"
                          />
                        </div>
                        <div className="flex gap-4">
                          <input
                            type="text"
                            value={editingFAQ.tags?.join(', ') || ''}
                            onChange={(e) => setEditingFAQ({ ...editingFAQ, tags: e.target.value.split(',').map(t => t.trim()) })}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                            placeholder="Tags (comma separated)"
                          />
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editingFAQ.is_published}
                              onChange={(e) => setEditingFAQ({ ...editingFAQ, is_published: e.target.checked })}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Published</span>
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button type="submit" className="px-4 py-2 bg-dusky-purple text-white rounded-lg hover:bg-dusky-purple">
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingFAQ(null)}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {faq.category && (
                                <span className="px-3 py-1 bg-dusky-purple/10 text-dusky-purple text-sm rounded-full">
                                  {faq.category.name}
                                </span>
                              )}
                              <span className="text-sm text-gray-500">
                                Last updated: {new Date(faq.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-super-dark-purple mb-2">{faq.question}</h3>
                            <p className="text-gray-700 whitespace-pre-wrap">{faq.answer}</p>
                            {faq.source_reference && (
                              <p className="mt-2 text-sm text-gray-500 italic">Source: {faq.source_reference}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          <div className="flex items-center gap-6 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              <span>{faq.view_count} views</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <ThumbsUp className="w-4 h-4 text-green-600" />
                              <span>{faq.helpful_count} found helpful</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingFAQ(faq)}
                              className="p-2 text-dusky-purple hover:bg-dusky-purple/10 rounded transition"
                              title="Edit"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteFAQ(faq.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                              title="Delete"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'add' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-super-dark-purple mb-6">Add New FAQ</h2>
            <form onSubmit={handleCreateFAQ} className="bg-white border border-gray-200 rounded-lg p-8 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
                <input
                  type="text"
                  required
                  value={newFAQ.question}
                  onChange={(e) => setNewFAQ({ ...newFAQ, question: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue"
                  placeholder="e.g., How do I open the RCSA template?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer *</label>
                <textarea
                  required
                  value={newFAQ.answer}
                  onChange={(e) => setNewFAQ({ ...newFAQ, answer: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue"
                  rows={6}
                  placeholder="Provide a detailed answer..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={newFAQ.category_id}
                    onChange={(e) => setNewFAQ({ ...newFAQ, category_id: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue"
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Source Reference</label>
                  <input
                    type="text"
                    value={newFAQ.source_reference}
                    onChange={(e) => setNewFAQ({ ...newFAQ, source_reference: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue"
                    placeholder="e.g., User Guide v2.3, Page 12"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags (comma separated)</label>
                <input
                  type="text"
                  value={newFAQ.tags}
                  onChange={(e) => setNewFAQ({ ...newFAQ, tags: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue"
                  placeholder="e.g., template, excel, getting-started"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={newFAQ.is_published}
                  onChange={(e) => setNewFAQ({ ...newFAQ, is_published: e.target.checked })}
                  className="w-4 h-4 text-dusky-purple rounded"
                />
                <label htmlFor="is_published" className="text-sm font-medium text-gray-700">
                  Publish immediately
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3 bg-gradient-to-r from-electric-blue to-dusky-purple text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Creating FAQ...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Create FAQ
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'bulk' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-super-dark-purple mb-6">Bulk Upload FAQs</h2>

            <div className="mb-8 p-6 bg-gradient-to-br from-blue-50 to-electric-blue/5 border border-electric-blue/20 rounded-lg">
              <h3 className="font-semibold text-super-dark-purple mb-3">Upload Instructions</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Upload a CSV or Excel file with FAQ data</li>
                <li>• Required columns: Question, Answer</li>
                <li>• Optional columns: Category, Source Reference, Tags, Is Published</li>
                <li>• Download the template below to see the correct format</li>
              </ul>
              <button
                onClick={downloadTemplate}
                className="mt-4 px-4 py-2 bg-gradient-to-r from-electric-blue to-dusky-purple text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
            </div>

            <form onSubmit={handleBulkUpload} className="bg-white border border-gray-200 rounded-lg p-8 space-y-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="bulk-upload"
                />
                <label
                  htmlFor="bulk-upload"
                  className="cursor-pointer px-6 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition inline-block"
                >
                  Choose File
                </label>
                {bulkFile && (
                  <p className="mt-4 text-sm text-gray-600">
                    Selected: <span className="font-medium">{bulkFile.name}</span>
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!bulkFile || uploadingBulk}
                className="w-full px-6 py-3 bg-gradient-to-r from-electric-blue to-dusky-purple text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploadingBulk ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload FAQs
                  </>
                )}
              </button>
            </form>

            {bulkResult && (
              <div className="mt-8 p-6 bg-gradient-to-br from-white to-gray-50 border-2 border-electric-blue/20 rounded-lg shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-super-dark-purple">Upload Results</h3>
                  <button
                    onClick={() => setBulkResult(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <div className="text-3xl font-bold text-blue-700">{bulkResult.total}</div>
                    <div className="text-xs text-blue-600 font-medium mt-1">Total Rows</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                    <div className="text-3xl font-bold text-green-700">{bulkResult.created}</div>
                    <div className="text-xs text-green-600 font-medium mt-1">✓ Created</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200">
                    <div className="text-3xl font-bold text-yellow-700">{bulkResult.duplicates}</div>
                    <div className="text-xs text-yellow-600 font-medium mt-1">⚠ Duplicates</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                    <div className="text-3xl font-bold text-red-700">{bulkResult.failed}</div>
                    <div className="text-xs text-red-600 font-medium mt-1">✗ Failed</div>
                  </div>
                  {bulkResult.categoriesCreated > 0 && (
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                      <div className="text-3xl font-bold text-purple-700">{bulkResult.categoriesCreated}</div>
                      <div className="text-xs text-purple-600 font-medium mt-1">+ New Categories</div>
                    </div>
                  )}
                </div>

                {bulkResult.created > 0 && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium">
                      ✓ Successfully uploaded {bulkResult.created} of {bulkResult.total} FAQ{bulkResult.total !== 1 ? 's' : ''}
                      {bulkResult.categoriesCreated > 0 && ` and created ${bulkResult.categoriesCreated} new categor${bulkResult.categoriesCreated !== 1 ? 'ies' : 'y'}`}
                    </p>
                  </div>
                )}

                {bulkResult.errors.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-super-dark-purple mb-3 flex items-center gap-2">
                      <span className="text-red-600">⚠</span>
                      Issues Found ({bulkResult.errors.length})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {bulkResult.errors.map((err, idx) => (
                        <div key={idx} className="p-3 bg-white border-l-4 border-red-500 rounded text-sm shadow-sm">
                          <div className="flex items-start gap-2">
                            <span className="text-red-600 font-bold">Row {err.row}:</span>
                            <div className="flex-1">
                              {err.question && (
                                <div className="font-medium text-gray-900 mb-1">"{err.question}"</div>
                              )}
                              <div className="text-red-700">{err.error}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div>
            <h2 className="text-2xl font-bold text-super-dark-purple mb-6">FAQ Analytics</h2>

            {analytics ? (
              <div className="space-y-8">
                <div className="grid grid-cols-3 gap-6">
                  <div className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold">Total FAQs</h3>
                      <FileText className="w-8 h-8 opacity-75" />
                    </div>
                    <div className="text-3xl font-bold">{faqs.length}</div>
                  </div>

                  <div className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold">Categories</h3>
                      <BarChart3 className="w-8 h-8 opacity-75" />
                    </div>
                    <div className="text-3xl font-bold">{categories.length}</div>
                  </div>

                  <div className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold">Total Views</h3>
                      <Eye className="w-8 h-8 opacity-75" />
                    </div>
                    <div className="text-3xl font-bold">
                      {faqs.reduce((sum, faq) => sum + faq.view_count, 0)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-super-dark-purple mb-4">Top Viewed FAQs</h3>
                    <div className="space-y-3">
                      {analytics.topViewed.slice(0, 5).map((faq, idx) => (
                        <div key={faq.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex-1">
                            <div className="font-medium text-super-dark-purple">{faq.question}</div>
                            {faq.category && (
                              <div className="text-sm text-gray-500">{faq.category.name}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Eye className="w-4 h-4" />
                            <span className="font-semibold">{faq.view_count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-super-dark-purple mb-4">Most Helpful FAQs</h3>
                    <div className="space-y-3">
                      {analytics.topHelpful.slice(0, 5).map((faq, idx) => (
                        <div key={faq.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex-1">
                            <div className="font-medium text-super-dark-purple">{faq.question}</div>
                            {faq.category && (
                              <div className="text-sm text-gray-500">{faq.category.name}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <ThumbsUp className="w-4 h-4" />
                            <span className="font-semibold">{faq.helpful_count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-super-dark-purple mb-4">Category Distribution</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {analytics.categoryStats.map((stat: any) => (
                      <div key={stat.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="font-medium text-super-dark-purple">{stat.name}</div>
                        <div className="text-2xl font-bold text-dusky-purple">{stat.faq_count || 0}</div>
                        <div className="text-sm text-gray-500">FAQs</div>
                      </div>
                    ))}
                  </div>
                </div>

                {analytics.searchAnalytics.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-super-dark-purple mb-4">Top Search Queries</h3>
                    <div className="space-y-2">
                      {analytics.searchAnalytics.slice(0, 10).map((search: any, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <span className="text-super-dark-purple">{search.search_query}</span>
                          <span className="text-sm font-semibold text-gray-600">{search.count} searches</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-electric-blue"></div>
                <p className="mt-4 text-gray-600">Loading analytics...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'categories' && (
          <div>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-super-dark-purple mb-6">Manage FAQ Categories</h2>

              <div className="mb-8 bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-8 shadow-sm">
                <h3 className="text-lg font-semibold text-super-dark-purple mb-4">Add New Category</h3>
                <form onSubmit={handleCreateCategory} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
                      <input
                        type="text"
                        required
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                        placeholder="e.g., Template Usage"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                      <select
                        value={newCategory.icon}
                        onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                      >
                        <option value="BookOpen">BookOpen</option>
                        <option value="HelpCircle">HelpCircle</option>
                        <option value="FileText">FileText</option>
                        <option value="Settings">Settings</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                      rows={3}
                      placeholder="Brief description of this category..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={creatingCategory}
                    className="px-6 py-3 bg-gradient-to-r from-electric-blue to-dusky-purple text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingCategory ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Creating Category...
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        Create Category
                      </>
                    )}
                  </button>
                </form>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-super-dark-purple mb-4">Existing Categories</h3>
                {categories.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No categories found. Create your first category above.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {categories.map((category) => (
                      <div key={category.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="px-3 py-1 bg-dusky-purple/10 text-dusky-purple text-sm rounded-full font-medium">
                              {category.name}
                            </span>
                            {category.faq_count !== undefined && (
                              <span className="text-sm text-gray-500">{category.faq_count} FAQs</span>
                            )}
                          </div>
                          {category.description && (
                            <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded ${
                            category.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQManagement;
